import { Loader } from 'components';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

// `allowUserTypes` lets an org-scoped user type stand in for the permission — a shift supervisor
// reaches guard reports by virtue of being a supervisor AT THIS ORG (userData.orgs[orgId].type —
// activeOrg is the raw org doc and has no `type` field; a manager can be a supervisor at one org
// and not another), without also needing Manage Reports granted through an org role. The module
// check still applies either way: a type never opens a page for an org that doesn't have the
// module.
const RouteGuard = ({
    children,
    module,
    activeOrg,
    permission,
    activePermissions,
    allowUserTypes = [],
    alsoAllow = [],
    userData
}) => {
    const location = useLocation();

    // The org this guard last let the page through in.
    const orgAllowedIn = useRef(null);

    const loading = activePermissions === null || activeOrg === null;

    const allowedByType = allowUserTypes.includes(userData?.orgs?.[activeOrg?.org_id]?.type);
    const allowedByPermission = activePermissions?.includes(permission);

    const allowedByAlternative = alsoAllow.some(
        rule =>
            activePermissions?.includes(rule.permission) &&
            activeOrg?.modules?.includes(rule.module)
    );

    const blocked =
        !loading &&
        !allowedByAlternative &&
        ((!allowedByPermission && !allowedByType) || !activeOrg?.modules?.includes(module));

    // The active org changed under this page (e.g. the user was taken off the org they were in) and
    // the page does not exist in the new one. That is not a missing page: send the user to the
    // root, which opens a page the new org has. Only when there is a new org to go to, and only
    // for a page the user was let into before, so a page that is simply not allowed still answers 404.
    const orgChangedUnderPage =
        blocked &&
        orgAllowedIn.current !== null &&
        Boolean(activeOrg?.org_id) &&
        orgAllowedIn.current !== activeOrg.org_id;

    // The root picks the page from the user document as well as the active org, so it waits until
    // the user document is in the new org too.
    const userIsInActiveOrg = userData?.active_org_id === activeOrg?.org_id;

    useEffect(() => {
        if (!loading && !blocked && activeOrg?.org_id) {
            orgAllowedIn.current = activeOrg.org_id;
        }
    }, [loading, blocked, activeOrg?.org_id]);

    if (loading) {
        return <Loader />;
    }

    if (orgChangedUnderPage) {
        return userIsInActiveOrg ? <Navigate to="/" replace /> : <Loader />;
    }

    if (blocked) {
        return <Navigate to="/maintenance/404" state={{ from: location }} />;
    }

    return children;
};

const mapStateToProps = ({ user }) => {
    const { activeOrg, activePermissions, userData } = user;
    return { activeOrg, activePermissions, userData };
};

export default connect(mapStateToProps, {})(RouteGuard);
